import axios, { AxiosError, AxiosResponse } from 'axios';

// 定义错误响应接口
export interface ErrorResponse {
  error: string;
}

// 定义环境变量接口
interface ImportMetaEnv {
  VITE_API_BASE_URL?: string;
}

interface ImportMeta {
  env: ImportMetaEnv;
}

interface SearchOptionsResponse {
  tcr_names: string[];
  trav_genes: string[];
  trbv_genes: string[];
  mhc_a_alleles: string[];
  mhc_b_alleles: string[];
  pdb_ids: string[];
  epitope_genes: string[];
  epitope_mutations: string[];
  epitope_species: string[];
}

interface SearchResponse {
  count: number;
  results: TCRComplex[];
}

interface SequenceLengthStatsResponse {
  trav: {
    lengths: number[];
    counts: number[];
  };
  trbv: {
    lengths: number[];
    counts: number[];
  };
}

interface EpitopeGeneStatsResponse {
  genes: string[];
  counts: number[];
}

interface DatabaseStatsResponse {
  total_entries: number;
  tcr_count: number;
  functional_pairs: number;
  pdb_structures: number;
  [key: string]: number; // 添加索引签名以允许字符串索引
}

interface TimelineStatsResponse {
  years: number[];
  counts: number[];
  cumulativeCounts?: number[];
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'reactcr/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
});

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError<ErrorResponse>) => {
    console.error('API Error:', error);
    if (error.response) {
      const errorMessage = error.response.data?.error || error.response.statusText || 'Server error';
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error('Unable to connect to server. Please check your network connection.');
    } else {
      throw new Error('Request configuration error');
    }
  }
);

// 缓存配置
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

export interface SearchOptions {
  tcr_names: string[];
  trav_genes: string[];
  trbv_genes: string[];
  mhc_a_alleles: string[];
  mhc_b_alleles: string[];
  pdb_ids: string[];
  epitope_genes: string[];
  epitope_mutations: string[];
  epitope_species: string[];
}

export interface TCRComplex {
  complex_id: string;
  tcr_name: string;
  cdr3a: string;
  trav: string;
  traj: string;
  cdr3b: string;
  trbv: string;
  trbj: string;
  species: string;
  mhc_a: string;
  mhc_b: string;
  mhc_mutation: string | null;
  mhc_class: string;
  epitope_gene: string;
  epitope_sequence: string;
  epitope_species: string | null;
  epitope_mutation: string | null;
  affinity_2d: string | null;
  affinity_3d: string | null;
  catch_bond: string | null;
  ec50_1: string | null;
  e_1: string | null;
  co_receptor_1: string | null;
  confidence_level_1: string | null;
  ec50_2: string | null;
  e_2: string | null;
  co_receptor_2: string | null;
  confidence_level_2: string | null;
  ec50_3: string | null;
  e_3: string | null;
  co_receptor_3: string | null;
  confidence_level_3: string | null;
  function: string | null;
  disease: string | null;
  pdb_id: string | null;
  donor_source: string | null;
  cell_source: string | null;
  reference: string | null;
}

export interface SearchParams {
  tcr_name?: string;
  trav?: string;
  trbv?: string;
  mhc_class?: string;
  mhc_a?: string;
  mhc_b?: string;
  epitope_sequence?: string;
  epitope_gene?: string;
  epitope_mutation?: string;
  epitope_species?: string;
  has_affinity?: '0' | '1';
  has_function?: '0' | '1';
  evidence_method?: string;
  pdb_id?: string;
  cdr3_sequence?: string;
  reference?: string;
}

export const tcrApi = {
  // Get search options
  getSearchOptions: async (): Promise<SearchOptions> => {
    const cacheKey = 'search_options';
    const cached = cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    
    try {
      const response = await api.get<SearchOptionsResponse>('/tcr/search_options/');
      if (!response.data || Object.keys(response.data).length === 0) {
        throw new Error('No search options available. Please try again later.');
      }
      
      // 缓存结果
      cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
      
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching search options:', error);
      if (axios.isAxiosError<ErrorResponse>(error)) {
        if (error.response) {
          switch (error.response.status) {
            case 404:
              throw new Error('Search options endpoint not found. Please contact support.');
            case 500:
              throw new Error('Server error occurred while fetching search options. Please try again later.');
            default:
              throw new Error(`Failed to fetch search options: ${error.response.data?.error || error.message}`);
          }
        } else if (error.request) {
          throw new Error('Unable to connect to the server. Please check your network connection.');
        } else {
          throw new Error('Request configuration error. Please try again.');
        }
      }
      throw error;
    }
  },

  // Search TCR complexes
  searchTCR: async (params: SearchParams): Promise<TCRComplex[]> => {
    try {
      const response = await api.get<SearchResponse>('/tcr/search/', { params });
      return response.data.results;
    } catch (error) {
      console.error('Error searching TCR:', error);
      throw error;
    }
  },

  // Get TCR complex details
  getTCRDetail: async (complexId: string): Promise<TCRComplex> => {
    try {
      const response = await api.get<TCRComplex>(`/tcr/${complexId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching TCR detail:', error);
      throw error;
    }
  },

  // Search TCRs by disease
  searchByDisease: async (category: string, disease: string, page: number = 1, pageSize: number = 10): Promise<{ results: TCRComplex[]; count: number }> => {
    try {
      const response = await api.get<SearchResponse>('/tcr/search_by_disease/', {
        params: {
          category,
          disease,
          page,
          page_size: pageSize
        }
      });
      return {
        results: response.data.results,
        count: response.data.count
      };
    } catch (error) {
      console.error('Error searching by disease:', error);
      throw error;
    }
  },

  // Get sequence length statistics
  getSequenceLengthStats: async (): Promise<SequenceLengthStatsResponse> => {
    try {
      const response = await api.get<SequenceLengthStatsResponse>('/tcr/sequence_length_stats/');
      return response.data;
    } catch (error) {
      console.error('Error fetching sequence length statistics:', error);
      throw error;
    }
  },

  // Get epitope gene statistics
  getEpitopeGeneStats: async (): Promise<EpitopeGeneStatsResponse> => {
    try {
      const response = await api.get<EpitopeGeneStatsResponse>('/tcr/epitope_gene_stats/');
      return response.data;
    } catch (error) {
      console.error('Error fetching epitope gene statistics:', error);
      throw error;
    }
  },

  // Get database statistics
  getDatabaseStats: async (): Promise<DatabaseStatsResponse> => {
    const cacheKey = 'database_stats'
    const cached = cache.get(cacheKey)
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data
    }
    
    try {
      const response = await api.get<DatabaseStatsResponse>('/tcr/database_stats/')
      cache.set(cacheKey, { data: response.data, timestamp: Date.now() })
      return response.data
    } catch (error) {
      console.error('Error in getDatabaseStats:', error)
      throw error
    }
  },

  // Get TCR timeline statistics
  getTCRTimelineStats: async (): Promise<TimelineStatsResponse> => {
    try {
      const response = await api.get<TimelineStatsResponse>('/tcr/tcr_timeline_stats/');
      return response.data;
    } catch (error) {
      console.error('Error fetching TCR timeline statistics:', error);
      throw error;
    }
  },

  // Get epitope timeline statistics
  getEpitopeTimelineStats: async (): Promise<TimelineStatsResponse> => {
    try {
      const response = await api.get<TimelineStatsResponse>('/tcr/epitope_timeline_stats/');
      return response.data;
    } catch (error) {
      console.error('Error fetching epitope timeline statistics:', error);
      throw error;
    }
  }
}; 